import { formatMoneyKr, formatNumber } from '../../utils';
import { useProject } from '../../project/project-hooks/useProject';
import styles from './project-progress.module.less';
import { ProgressbarWithGoals } from '../ProgressBarWithGoals/ProgressBarWithGoals';
import { getPercentageFundedForProject } from '../../project/project-model';
import { useMemo } from 'react';
import Image from '../image/Image';

export const ProjectProgress = ({ className = '' }) => {
    const project = useProject();

    const matchingCollectedAmount = parseInt(
        project.matchingGift?.amountDoubled ?? project.matchingGift?.paidAmount ?? 0,
        10
    );
    const collectedAmountWithoutMatching = project.collected_amount - matchingCollectedAmount;

    const matchingGiftText = useMemo(() => {
        if (!project?.matchingGift) {
            return null;
        }

        const { matching, orgName, amountDoubled, profile, maxAmount, paidAmount } = project.matchingGift;
        if (profile?.progressText) {
            return `${profile?.progressText} (${formatMoneyKr(amountDoubled)})`;
        }
        return matching
            ? `${orgName} dobler hver krone du gir (${formatMoneyKr(amountDoubled)} av ${formatMoneyKr(maxAmount)})`
            : `${orgName} har støttet innsamlingen med ${formatMoneyKr(paidAmount)}`;
    }, [
        project?.matchingGift?.matching,
        project?.matchingGift?.orgName,
        project?.matchingGift?.amountDoubled,
        project?.matchingGift?.maxAmount,
        project?.matchingGift?.paidAmount,
        project?.matchingGift?.profile?.progressText,
    ]);

    if (!project.moneygoal) {
        return null;
    }

    const totalPercentOfMoneygoalReached = getPercentageFundedForProject({ project });

    const maxMoneyGoal = project.project_goals?.reduce((acc, val) => (val.amount > acc ? val.amount : acc), 0);
    const maxMoneyGoalFundedPercent = (project.collected_amount / maxMoneyGoal) * 100;

    const projectHasExtraGoals = project.project_goals?.length > 0;

    return (
        <div className={[styles.progress, className].join(' ')}>
            <ProgressbarWithGoals
                goals={project.project_goals}
                moneygoal={project.moneygoal}
                collectedAmount={project.collected_amount}
                collectedAmountWithoutMatching={collectedAmountWithoutMatching}
                matchingCollectedAmount={matchingCollectedAmount}
            />
            <div className={styles.goalText}>
                <div>
                    {projectHasExtraGoals && (
                        <>
                            <b>Hovedmål</b>
                            <br />
                        </>
                    )}
                    {!projectHasExtraGoals || totalPercentOfMoneygoalReached < 100 ? (
                        <span>
                            {formatNumber(totalPercentOfMoneygoalReached)}% av {formatMoneyKr(project.moneygoal)}
                        </span>
                    ) : (
                        <span>
                            {formatMoneyKr(project.moneygoal)}{' '}
                            <Image
                                className={styles.goalReachedIcon}
                                src="checkmark-filled.svg"
                                alt="Sjekkmerke for nådd hovedmål"
                            />
                        </span>
                    )}
                </div>
                {projectHasExtraGoals && (
                    <div className={styles.bonusGoalText}>
                        <b>Bonusmål</b>
                        <br />
                        <span>
                            {formatNumber(maxMoneyGoalFundedPercent)}% av {formatMoneyKr(maxMoneyGoal)}
                        </span>
                    </div>
                )}
            </div>
            {project.matchingGift && !project.matchingGift?.profile?.hideFromProjectProgress && (
                <div className={styles.matchingText}>
                    <span>
                        <span className={styles.giversIcon} />
                        Givere ({formatMoneyKr(collectedAmountWithoutMatching)})
                    </span>
                    <span className={styles.matchingWrapper}>
                        <span className={styles.matcherIcon} />
                        {matchingGiftText}
                    </span>
                </div>
            )}
        </div>
    );
};
export default ProjectProgress;
